import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { formatTimeAgo } from '../utils/moderation';

const UserProfilePanel = ({ username, currentUser, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);

  const isOwnProfile = currentUser?.username === username;

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    setLoading(true);
    const { data: user } = await supabase
      .from('feed_users')
      .select('*')
      .eq('username', username)
      .single();

    if (user) {
      setProfile(user);

      const { data: userPosts } = await supabase
        .from('posts')
        .select('*')
        .eq('author_id', user.id)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .limit(5);
      setPosts(userPosts || []);

      if (currentUser && !isOwnProfile) {
        const { data: follow } = await supabase
          .from('follows')
          .select('follower_id')
          .eq('follower_id', currentUser.id)
          .eq('following_id', user.id)
          .single();
        setIsFollowing(!!follow);
      }
    }
    setLoading(false);
  };

  const handleFollow = async () => {
    if (!currentUser || isOwnProfile) return;

    if (isFollowing) {
      await supabase.from('follows').delete()
        .eq('follower_id', currentUser.id)
        .eq('following_id', profile.id);
      await supabase.from('feed_users').update({ follower_count: Math.max(0, profile.follower_count - 1) }).eq('id', profile.id);
      await supabase.from('feed_users').update({ following_count: Math.max(0, currentUser.following_count - 1) }).eq('id', currentUser.id);
      setProfile(p => ({ ...p, follower_count: Math.max(0, p.follower_count - 1) }));
    } else {
      await supabase.from('follows').insert({ follower_id: currentUser.id, following_id: profile.id });
      await supabase.from('feed_users').update({ follower_count: (profile.follower_count || 0) + 1 }).eq('id', profile.id);
      await supabase.from('feed_users').update({ following_count: (currentUser.following_count || 0) + 1 }).eq('id', currentUser.id);
      setProfile(p => ({ ...p, follower_count: (p.follower_count || 0) + 1 }));

      // Notification
      await supabase.from('notifications').insert({
        recipient_id: profile.id,
        type: 'follow',
        actor_username: currentUser.username,
        message: `${currentUser.username} started following you`,
      });
    }
    setIsFollowing(!isFollowing);
  };

  if (loading) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
      <div className="card" style={{ padding: 32 }}><p>Loading profile...</p></div>
    </div>
  );

  if (!profile) return null;

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 500, backdropFilter: 'blur(3px)' }} onClick={onClose} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        zIndex: 501, width: '90%', maxWidth: 460, maxHeight: '85vh', overflowY: 'auto',
        animation: 'dropIn 0.2s ease'
      }}>
        <div className="card" style={{ padding: '28px 28px' }}>
          <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1.2rem' }}>✕</button>

          {/* Avatar + name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: 'var(--primary-light)', color: 'var(--primary-color)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: '1.5rem', border: '3px solid var(--primary-color)'
            }}>
              {profile.username[0].toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, marginBottom: 4 }}>@{profile.username}</h3>
              {profile.bio && <p style={{ fontSize: '0.875rem', margin: 0 }}>{profile.bio}</p>}
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 20, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--border-color)' }}>
            {[
              { label: 'Posts', value: profile.post_count || 0 },
              { label: 'Followers', value: profile.follower_count || 0 },
              { label: 'Following', value: profile.following_count || 0 },
            ].map((stat, i) => (
              <div key={stat.label} style={{
                flex: 1, padding: '12px 8px', textAlign: 'center',
                borderRight: i < 2 ? '1px solid var(--border-color)' : 'none'
              }}>
                <div style={{ fontFamily: 'DM Serif Display, serif', fontSize: '1.4rem', color: 'var(--text-color)' }}>{stat.value}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Interests */}
          {profile.interests?.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 8 }}>Interests</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {profile.interests.map(interest => (
                  <span key={interest} style={{
                    padding: '3px 10px', borderRadius: 'var(--radius-full)',
                    background: 'var(--secondary-light)', color: 'var(--secondary-color)',
                    fontSize: '0.78rem', fontWeight: 600
                  }}>{interest}</span>
                ))}
              </div>
            </div>
          )}

          {/* Follow button */}
          {!isOwnProfile && currentUser && (
            <button
              onClick={handleFollow}
              className="btn"
              style={{
                width: '100%', marginBottom: 20,
                background: isFollowing ? 'var(--bg-secondary)' : 'var(--primary-color)',
                color: isFollowing ? 'var(--text-color)' : '#fff',
                border: isFollowing ? '1.5px solid var(--border-color)' : 'none',
              }}
            >
              {isFollowing ? '✓ Following' : '+ Follow'}
            </button>
          )}

          {/* Recent posts */}
          {posts.length > 0 && (
            <div>
              <p style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 10 }}>Recent Posts</p>
              {posts.map(post => (
                <div key={post.id} style={{
                  padding: '10px 14px', borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)',
                  marginBottom: 8
                }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-color)', margin: 0, lineHeight: 1.5 }}>
                    {post.content.length > 100 ? post.content.slice(0, 100) + '...' : post.content}
                  </p>
                  <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    ▲ {post.upvotes - post.downvotes} · {formatTimeAgo(post.created_at)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UserProfilePanel;